import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import api from "../services/api";
import { toastError, toastSuccess } from "../utils/hotToast";
import ExamReportGenerator from "../components/ExamReportGenerator";

export default function AdminCompletedExams() {
  const navigate = useNavigate();

  const [examSummaries, setExamSummaries] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reopeningExamId, setReopeningExamId] = useState(null);

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedExamForReport, setSelectedExamForReport] = useState(null);
  const [copiesForModals, setCopiesForModals] = useState([]);
  const [loadingReportExamId, setLoadingReportExamId] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [summariesRes, usersRes] = await Promise.all([
        api.get("/admin/exam-summaries"),
        api.get("/admin/users"),
      ]);
      setExamSummaries(summariesRes.data || []);
      setUsers(usersRes.data || []);
    } catch (err) {
      toastError(`Failed to load completed exams: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const completedExams = useMemo(
    () =>
      examSummaries
        .filter((exam) => exam.evaluationCompleted)
        .filter((exam) =>
          (exam.title || "").toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
          const aTs = a.evaluationCompletedAt
            ? new Date(a.evaluationCompletedAt).getTime()
            : 0;
          const bTs = b.evaluationCompletedAt
            ? new Date(b.evaluationCompletedAt).getTime()
            : 0;
          return bTs - aTs;
        }),
    [examSummaries, searchTerm]
  );

  const handleReopenEvaluation = async (exam) => {
    const ok = window.confirm(
      "Reopen this exam evaluation? Examiners will be able to access and work on copies again."
    );
    if (!ok) return;

    setReopeningExamId(exam._id);
    try {
      await api.patch(`/admin/exams/${exam._id}/reopen-evaluation`);
      toastSuccess("Exam evaluation reopened.");
      fetchData();
    } catch (err) {
      toastError(err.response?.data?.message || "Failed to reopen exam evaluation.");
    } finally {
      setReopeningExamId(null);
    }
  };

  const handleOpenReportModal = async (exam) => {
    setLoadingReportExamId(exam._id);
    try {
      const res = await api.get(`/admin/exams/${exam._id}/copies`);
      setCopiesForModals(res.data.copies || []);
      setSelectedExamForReport(res.data.exam);
      setIsReportModalOpen(true);
    } catch (err) {
      toastError("Failed to load report data.");
    } finally {
      setLoadingReportExamId(null);
    }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 bg-white min-h-screen" style={{ fontFamily: "Dosis, sans-serif" }}>
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">Completed Exams Pool</h1>
          <p className="text-sm text-gray-600 mt-1">
            Exams that were marked evaluation-completed. Reopen any exam when needed.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => navigate("/admin/deleted-exams")}
            className="inline-flex items-center px-4 py-2 rounded-xl border-2 border-gray-900 text-gray-900 hover:bg-gray-100 transition font-semibold text-sm"
          >
            Deleted Exams
          </button>
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center px-4 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition font-semibold text-sm"
          >
            Back to Admin Panel
          </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border-2 border-gray-900">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div className="relative w-full lg:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search completed exams..."
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-gray-600">Loading completed exams...</div>
        ) : completedExams.length === 0 ? (
          <div className="text-gray-600">No completed exams found.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Closed At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Copies</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {completedExams.map((exam) => (
                  <tr key={exam._id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{exam.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{exam.course || "N/A"}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {exam.evaluationCompletedAt
                        ? new Date(exam.evaluationCompletedAt).toLocaleString()
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{exam.statusCounts?.total || 0}</td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <a
                          href={`/admin/exams/${exam._id}`}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg text-white bg-gray-900 hover:bg-gray-800"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" /> View
                        </a>

                        {(exam.statusCounts?.total || 0) > 0 && (
                          <button
                            onClick={() => handleOpenReportModal(exam)}
                            disabled={loadingReportExamId === exam._id}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50"
                          >
                            {loadingReportExamId === exam._id ? (
                              <>
                                <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" /> Loading...
                              </>
                            ) : (
                              <>
                                <DocumentArrowDownIcon className="h-4 w-4 mr-1" /> Report
                              </>
                            )}
                          </button>
                        )}

                        <button
                          onClick={() => handleReopenEvaluation(exam)}
                          disabled={reopeningExamId === exam._id}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg text-white bg-amber-700 hover:bg-amber-800 disabled:opacity-50"
                        >
                          {reopeningExamId === exam._id ? (
                            <>
                              <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" /> Reopening...
                            </>
                          ) : (
                            <>
                              <ArrowUturnLeftIcon className="h-4 w-4 mr-1" /> Reopen
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ExamReportGenerator
        isOpen={isReportModalOpen}
        onClose={() => {
          setIsReportModalOpen(false);
          setSelectedExamForReport(null);
          setCopiesForModals([]);
        }}
        exam={selectedExamForReport}
        copies={copiesForModals}
        users={users}
      />
    </div>
  );
}
